import { Helmet } from "react-helmet-async";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CallToAction from "@/components/CallToAction";
import { Tractor, Shield, Users, GraduationCap, Heart, Building, Baby, Briefcase } from "lucide-react";

const allIssues = [
  {
    icon: Tractor,
    title: "Rural Representation",
    description: "Matt's campaign is rooted in the belief that the 33rd District—comprising Milford, Frederica, Houston, Bowers, South Bowers, and Magnolia—needs representation that will advocate for our values clearly in Dover against a state legislature that doesn't always have us in mind. As the 'at-large' school board member representing an entire district, Matt has provided constituent services to parents and taxpayers for years.",
    points: [
      "Advocate for and help the constituents of the 33rd District",
      "Fight for rural and small town interests",
      "Protect the 'Right to Farm' against encroachment from the Legislature",
      "Advocate and protect Delaware's unique heritage of volunteer firefighters"
    ]
  },
  {
    icon: Briefcase,
    title: "Fight for the Delaware Economy",
    description: "Matt believes the Delaware government is rapidly creating a hostile and uncertain environment for our corporate partners, while at the same time making it tough for our local businesses to operate.",
    points: [
      "Introduce a 'Regulatory Reform Act', whereby state government agencies must justify existing and any proposed regulations, and sunset any regulations that produce no benefit. Regulatory agencies must also be compelled to survey and quantify the cost of compliance to Delaware small businesses and farms.",
      "I will oppose any new taxes of any kind. Delaware does not have a funding problem; it has a spending problem. New spending has increased at 150% of the rate of new revenue over the past few years; this is irresponsible and unsustainable. I will oppose any state budget that is not balanced or fair to the Delaware taxpayer.",
      "I will fight to protect Delaware's singular system of corporate governance, which provides over one-third of our revenue at almost zero cost to Delawareans."
    ]
  },
  {
    icon: Shield,
    title: "Second Amendment Rights",
    description: "As a firearm owner, and a life member of the DSSA (Delaware State Sportsmen's Association) and the NRA, Matt is a vocal and published defender of gun rights, arguing that gun control legislation often targets law-abiding citizens through incrementalism aimed at chipping away constitutional protections.",
    points: [
      "Defend the rights of our citizens as established in both the Delaware Constitution and United States Constitution",
      "Oppose incremental restrictions on law-abiding citizens",
      "Protect hunting and sporting traditions",
      "Stand against government overreach"
    ]
  },
  {
    icon: Baby,
    title: "Advocate for Children and for the Sanctity of Life",
    description: "As a member of the Delaware Family Policy Council, a long-time volunteer and advocate for the rights of parents and children, and a lifelong pro-life conservative, Matt supports the rights of babies to be born and to BE.",
    points: [
      "Will support any effort in the General Assembly to protect innocent life",
      "Believes the accepted scientific certainty that human life begins upon conception",
      "Advocated for and helped write the bill to strip taxpayer-funded state pensions away from education employees who are convicted of child abuse (HB113)"
    ]
  },
  {
    icon: Users,
    title: "Student Discipline & Safety",
    description: "Describing student discipline as 'off-the-rails,' Matt supports establishing more alternative schools for students with extreme behavioral issues to maintain safety in mainstream classrooms. Matt also has been a fierce public advocate for hardening and securing schools, including through support of armed SROs (school resource officers) and constables.",
    points: [
      "Maintain safety and children's ability to have an ordered, structured classroom environment by removing students who have extreme or recurring behavioral issues",
      "Expand placements at alternative schools for at-risk students",
      "Helped write a bill to fund safety & security personnel the same way we fund teachers (HB167 and HB32)",
      "Stop putting roadblocks in the way of schools and front line teachers, give them the tools and the authority to enforce order instead"
    ]
  },
  {
    icon: Building,
    title: "Restore Local Control & Stop Unfunded Mandates",
    description: "Matt is critical of legislative initiatives that increase administrative bloat by imposing compliance costs on districts without providing necessary funding. Over the last decade, authority has shifted to state bureaucracy while education costs have skyrocketed. Matt advocates for returning financial decision-making power to local communities.",
    points: [
      "Prioritize classroom spending over bureaucracy",
      "Reduce administrative compliance burdens & lower costs",
      "Protect school budgets from unfunded requirements from the state government",
      "Return flexibility and authority to the local communities in spending decisions"
    ]
  },
  {
    icon: GraduationCap,
    title: "Curriculum Neutrality",
    description: "Matt believes public schools must provide a perspective-neutral learning environment and remove ideology from the classrooms and the curriculum. Matt was the architect of a first-of-its-kind policy in Delaware public schools that removed ideological banners, flags, and symbols from the classrooms and required unbiased teaching of curriculum.",
    quote: "The community has a right to a prospective neutral learning environment without political or quasi-political, ideological, or social advocacy disguised as education or presented as inherently moral or as the only correct answer.",
    quoteSource: "Matt Bucher, Milford School Board Meeting, August 18th, 2024",
    points: [
      "Remove social advocacy disguised as education",
      "Require balanced presentation of multiple perspectives",
      "Focus on academic standards, not political affirmation",
      "Ensure perspective-neutral classrooms"
    ]
  },
  {
    icon: Heart,
    title: "Election Integrity",
    description: "Matt supports strong election integrity measures, including voter ID laws, asserting that ensuring secure elections is a primary concern for reasonable citizens. He supports requiring photo ID at the polling place, and opposes mail-in voting with no verification of identity.",
    points: [
      "Support voter ID requirements at polling places",
      "Oppose mail-in voting without identity verification",
      "Prevent ballot harvesting",
      "Ensure secure and trustworthy elections"
    ]
  }
];

const Issues = () => {
  return (
    <>
      <Helmet>
        <title>Issues | Matt Bucher for State Representative</title>
        <meta name="description" content="Matt Bucher's conservative platform: education reform, local control, defending Second Amendment rights, election integrity, and fighting for rural Delaware." />
        <link rel="canonical" href="https://votebucher.com/issues" />
        <meta property="og:url" content="https://votebucher.com/issues" />
      </Helmet>
      
      <div className="min-h-screen flex flex-col">
        <Header />
        
        <main id="main-content" className="flex-grow">
          {/* Hero Banner */}
          <section className="bg-secondary py-20" aria-labelledby="issues-heading">
            <div className="container mx-auto px-4 text-center">
              <h1 id="issues-heading" className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-secondary-foreground mb-4">
                The Issues
              </h1>
              <p className="font-body text-xl text-secondary-foreground/80 max-w-2xl mx-auto">
                Conservative solutions for the 33rd District's challenges
              </p>
            </div>
          </section>

          {/* Issues Grid */}
          <section className="py-16 bg-background" aria-label="Policy positions">
            <div className="container mx-auto px-4">
              <div className="space-y-16">
                {allIssues.map((issue, index) => (
                  <article 
                    key={issue.title}
                    className={`grid grid-cols-1 lg:grid-cols-2 gap-8 items-start ${
                      index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                    }`}
                  >
                    <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center" aria-hidden="true">
                          <issue.icon className="h-8 w-8 text-primary" aria-hidden="true" />
                        </div>
                        <h2 className="font-display text-3xl font-bold text-foreground">
                          {issue.title}
                        </h2>
                      </div>
                      <p className="font-body text-lg text-muted-foreground mb-6">
                        {issue.description}
                      </p>
                      {issue.quote && (
                        <blockquote className="border-l-4 border-primary pl-4 mb-6">
                          <p className="font-body text-lg italic text-foreground mb-2">
                            "{issue.quote}"
                          </p>
                          <cite className="font-body text-sm text-muted-foreground not-italic">
                            — {issue.quoteSource}
                          </cite>
                        </blockquote>
                      )}
                    </div>
                    <div className={`bg-card p-6 rounded-lg border border-border shadow-sm ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                      <h3 className="font-display text-lg font-bold text-foreground mb-4">
                        Matt's Priorities:
                      </h3>
                      <ul className="space-y-3" role="list">
                        {issue.points.map((point, i) => (
                          <li key={i} className="flex items-start gap-3">
                            <span className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0" aria-hidden="true" />
                            <span className="font-body text-muted-foreground">{point}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </section>

          <CallToAction />
        </main>
        
        <Footer />
      </div>
    </>
  );
};

export default Issues;
